import React, { useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-hot-toast';
import { X, Users, UserPlus, Settings } from 'lucide-react';
import { selectTenant } from '../services/integrationService';
import { useAdmin } from '../context/AdminContext';

const AuthorizationModal = ({ isOpen, onClose, action = "access this feature" }) => {
  const { keycloak } = useKeycloak();
  const { refreshAdminStatus } = useAdmin();
  const [adminEmail, setAdminEmail] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [showTenantForm, setShowTenantForm] = useState(false);

  const userDomain = keycloak.tokenParsed?.email?.split('@')[1];

  const handleJoinTenant = async (e) => {
    e.preventDefault();

    if (!adminEmail.trim()) {
      toast.error('Please enter an admin email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate domain match
    const adminDomain = adminEmail.split('@')[1];
    if (userDomain !== adminDomain) {
      toast.error(`Cannot join tenant from different domain. Your domain: ${userDomain}`);
      return;
    }

    try {
      setIsJoining(true);

      // Call the select tenant API
      await selectTenant(adminEmail, keycloak.token);

      // Refresh admin status to update the context
      await refreshAdminStatus();

      // Update authorization status
      localStorage.setItem('isUserAuthorized', 'true');
      sessionStorage.setItem('wasAuthorized', 'true');

      toast.success('Successfully joined tenant! You can now access the feature.');
      onClose();
      
      // Refresh the page to update the UI
      window.location.reload();
      
    } catch (error) {
      console.error('Tenant selection error:', error);
      
      if (error.response?.status === 404) {
        toast.error('Admin not found. Please check the email address.');
      } else if (error.response?.status === 400) {
        toast.error('Cannot join tenant from different domain');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to join tenant. Please try again.');
      }
    } finally {
      setIsJoining(false);
    }
  };

  const handleCreateTenant = () => {
    onClose();
    window.location.href = '/integrations';
  };

  const handleContactAdmin = () => {
    const subject = encodeURIComponent('Access Request');
    const body = encodeURIComponent(`Hi, I need access to the application. My email is ${keycloak.tokenParsed?.email}.`);
    window.open(`mailto:support@${userDomain}?subject=${subject}&body=${body}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-red-600">Authorization Required</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800 font-medium">
              You don't have authorization to {action}
            </p>
            <p className="text-red-700 text-sm mt-1">
              Please select one of the options below to get access
            </p>
          </div>
        </div>

        {!showTenantForm ? (
          <div className="space-y-3">
            <button
              onClick={() => setShowTenantForm(true)}
              className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Users size={18} className="mr-2" />
              Join Existing Tenant
            </button>

            <button
              onClick={handleCreateTenant}
              className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Settings size={18} className="mr-2" />
              Create New Tenant (Become Admin)
            </button>

            <button
              onClick={handleContactAdmin}
              className="w-full flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <UserPlus size={18} className="mr-2" />
              Contact Support
            </button>
          </div>
        ) : (
          <form onSubmit={handleJoinTenant} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email Address
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={`admin@${userDomain}`}
                required
                disabled={isJoining}
              />
              <p className="mt-1 text-xs text-gray-500">
                Must be from your domain ({userDomain})
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowTenantForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={isJoining}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isJoining || !adminEmail.trim()}
                className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center ${
                  isJoining ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isJoining ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Joining...
                  </>
                ) : (
                  'Join Tenant'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthorizationModal;